import type { Member } from "@/types/Member";
import { isElevatedUser } from "@/utils/userPermissions";

/**
 * R1 (hearth/docs/BOOKING_RULES.md#r1) — maximum future booking date by role.
 *   - Members: 2 calendar months from today.
 *   - Elevated users (admin / superadmin / PM): 52 weeks (365 days).
 *
 * The helper returns the LAST date that can be booked (inclusive) — anything strictly
 * after this date must be rejected. Time-of-day on `today` is irrelevant; the helper
 * normalises to start-of-day so the same date can always be compared cleanly.
 */
const MEMBER_MAX_MONTHS_OUT = 2;
const ELEVATED_MAX_DAYS_OUT = 365;

export function getMaxForwardBookingDate(member: Member | null | undefined, today: Date = new Date()): Date {
  const start = new Date(today);
  start.setUTCHours(0, 0, 0, 0);
  const result = new Date(start);
  if (isElevatedUser(member)) {
    result.setUTCDate(result.getUTCDate() + ELEVATED_MAX_DAYS_OUT);
  } else {
    result.setUTCMonth(result.getUTCMonth() + MEMBER_MAX_MONTHS_OUT);
  }
  return result;
}

/**
 * Returns true when the proposed booking date sits strictly after the role-aware
 * maximum future date — i.e. it should be rejected.
 */
export function isBeyondMaxFutureForRole(
  date: Date,
  member: Member | null | undefined,
  today: Date = new Date(),
): boolean {
  const max = getMaxForwardBookingDate(member, today);
  const proposed = new Date(date);
  proposed.setUTCHours(0, 0, 0, 0);
  return proposed.getTime() > max.getTime();
}

/**
 * Calendar VIEW horizon — deliberately distinct from the (shorter, role-aware) bookable window
 * above. Every member (elevated or not) may VIEW the calendar through the end of the NEXT
 * calendar year, so slot/recurring bookings that run into the following year are visible — even
 * though NEW bookings stay capped at the role-aware bookable window (`getMaxForwardBookingDate`,
 * also enforced server-side per R1). Returns the last viewable day, inclusive, at end-of-day
 * local time (the calendar's nav math works in local time, not UTC).
 */
export function getCalendarViewMaxDate(today: Date = new Date()): Date {
  const d = new Date(today.getFullYear() + 1, 11, 31);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Returns the first day of the current month at UTC midnight (00:00:00.000Z).
 */
export function startOfCurrentMonth(today: Date = new Date()): Date {
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0, 0));
}

/**
 * Returns true when the given date is strictly before the first day of the current month.
 */
export function isBeforeStartOfCurrentMonth(date: Date, today: Date = new Date()): boolean {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime() < startOfCurrentMonth(today).getTime();
}
